import React, { useMemo, useState, useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { OnboardingCheckpointStep } from '../components/onboarding/OnboardingCheckpointStep';
import { MedicationSafetyCheckpoint } from '../components/onboarding/MedicationSafetyCheckpoint';
import { OnboardingQuestionStep } from '../components/onboarding/OnboardingQuestionStep';
import { OnboardingIntroStep } from '../components/onboarding/OnboardingIntroStep';
import {
  OnboardingCreateAccountStep,
  CreateAccountData,
} from '../components/onboarding/OnboardingCreateAccountStep';
import { OnboardingEmailVerificationStep } from '../components/onboarding/OnboardingEmailVerificationStep';
import { onboardingSections } from '../onboarding/sections';
import { shouldShowQuestion } from '../onboarding/utils';
import { SeekerOnboardingService } from '../services/onboardingService';
import { useSeekerAuth, useOnboardingSession } from '../hooks/useAuth';
import { OnboardingAnswers, QuestionDefinition, SectionDefinition } from '../types/onboarding';
import { getSupabaseClient } from '../lib/supabase';

type SeekerOnboardingRouteProps = {
  answers: OnboardingAnswers;
  setAnswers: React.Dispatch<React.SetStateAction<OnboardingAnswers>>;
};

type QuestionStep = {
  kind: 'question';
  id: string;
  section: SectionDefinition;
  question: QuestionDefinition;
};

type CheckpointStep = {
  kind: 'checkpoint';
  id: string;
  progressSection: SectionDefinition;
  title: string;
  subtitle: string;
  detailHeading?: string;
  detailBody?: string;
  nextHeading?: string;
  nextBody?: string;
  variant?: 'default' | 'medication';
  acknowledgements?: Array<{ id: string; label: string }>;
  showIf?: {
    questionId: string;
    equals?: string;
    includes?: string;
  };
};

type IntroStep = {
  kind: 'intro';
  id: string;
};

type CreateAccountStep = {
  kind: 'create-account';
  id: string;
  progressSection: SectionDefinition;
};

type EmailVerificationStep = {
  kind: 'email-verification';
  id: string;
  progressSection: SectionDefinition;
};

type StepDefinition =
  | QuestionStep
  | CheckpointStep
  | IntroStep
  | CreateAccountStep
  | EmailVerificationStep;

type CheckpointDefinition = {
  id: string;
  afterQuestionId: string;
  progressSectionId: string;
  title: string;
  subtitle: string;
  detailHeading?: string;
  detailBody?: string;
  nextHeading?: string;
  nextBody?: string;
  variant?: 'default' | 'medication';
  acknowledgements?: Array<{ id: string; label: string }>;
  showIf?: {
    questionId: string;
    equals?: string;
    includes?: string;
  };
};

const checkpointDefinitions: CheckpointDefinition[] = [
  {
    id: 'checkpoint-after-ssri',
    afterQuestionId: 'currently_on_meds',
    progressSectionId: 'medical-info',
    title: 'Important safety information about your medications',
    subtitle:
      'You shared that you are currently taking SSRIs, SNRIs, or another psychiatric medication. Thank you for letting us know - this information is essential for your safety.',
    detailHeading: 'How this affects your matching',
    detailBody:
      'Some psychiatric medications can affect how psychedelics work in the body, increase the risk of adverse reactions, and impact emotional or physiological responses during a journey. Because of these medication risks, you will only be matched with guides who have the specific training, experience, and protocols to support clients on psychiatric medications. Not all guides are able to work safely with medicated clients. Your matches may be fewer, but they will be safer and more appropriate for your needs.',
    nextHeading: 'Please Acknowledge the Following',
    nextBody:
      'To proceed, please confirm each statement below. Your honesty helps us take better care of you.',
    acknowledgements: [
      {
        id: 'ssri_ack_risks',
        label:
          'I understand that combining certain psychiatric medications with psychedelic experiences may increase risks, and I will discuss these risks openly with my matched guide.',
      },
      {
        id: 'ssri_ack_no_medical_advice',
        label:
          'I understand that Guides Collective cannot provide medical advice, adjust medications, or recommend tapering. Any medication decisions must be made with a licensed medical professional.',
      },
      {
        id: 'ssri_ack_matching_limits',
        label:
          'I understand that my matches may be limited to guides who are specifically trained to support clients on psychiatric medications.',
      },
    ],
    variant: 'medication',
    showIf: {
      questionId: 'currently_on_meds',
      equals: 'Yes',
    },
  },
];

const questionSteps: QuestionStep[] = onboardingSections.flatMap((section) =>
  section.questions.map<QuestionStep>((question) => ({
    kind: 'question',
    id: question.id,
    section,
    question,
  })),
);

const allSteps: StepDefinition[] = (() => {
  const steps: StepDefinition[] = [];
  for (const step of questionSteps) {
    steps.push(step);
    const matchingCheckpoints = checkpointDefinitions.filter(
      (checkpoint) => checkpoint.afterQuestionId === step.id,
    );
    for (const checkpoint of matchingCheckpoints) {
      const progressSection =
        onboardingSections.find((section) => section.id === checkpoint.progressSectionId) ??
        step.section;
      steps.push({
        kind: 'checkpoint',
        id: checkpoint.id,
        progressSection,
        title: checkpoint.title,
        subtitle: checkpoint.subtitle,
        detailHeading: checkpoint.detailHeading,
        detailBody: checkpoint.detailBody,
        nextHeading: checkpoint.nextHeading,
        nextBody: checkpoint.nextBody,
        variant: checkpoint.variant ?? 'default',
        acknowledgements: checkpoint.acknowledgements,
        showIf: checkpoint.showIf,
      });

    }

    // Add create-account and email-verification directly after location_postcode
    if (step.id === 'location_postcode') {
      const progressSection =
        onboardingSections.find((section) => section.id === 'experience-preferences') ??
        step.section;
      steps.push({
        kind: 'create-account',
        id: 'create-account',
        progressSection,
      });
      steps.push({
        kind: 'email-verification',
        id: 'email-verification',
        progressSection,
      });
    }
  }
  return steps;
})();

function isStepVisible(step: StepDefinition, answers: OnboardingAnswers) {
  if (step.kind === 'intro') return true;
  if (step.kind === 'create-account') return true;
  if (step.kind === 'email-verification') return true;
  if (step.kind === 'question') {
    return shouldShowQuestion(step.question, answers);
  }
  if (step.kind === 'checkpoint' && step.showIf) {
    const value = answers[step.showIf.questionId];
    if (Array.isArray(value) && step.showIf.includes) {
      return value.includes(step.showIf.includes);
    }
    if (typeof value === 'string') {
      if (step.showIf.equals && value !== step.showIf.equals) return false;
      if (step.showIf.includes && value !== step.showIf.includes) return false;
    } else if (step.showIf.equals || step.showIf.includes) {
      return false;
    }
  }
  return true;
}

function findNextVisibleIndex(
  start: number,
  direction: 1 | -1,
  answers: OnboardingAnswers,
): number {
  let index = start;
  while (index >= 0 && index < allSteps.length) {
    if (isStepVisible(allSteps[index], answers)) {
      return index;
    }
    index += direction;
  }
  return -1;
}

export function SeekerOnboardingRoute({ answers, setAnswers }: SeekerOnboardingRouteProps) {
  const { sectionId } = useParams<{ sectionId?: string }>();
  const navigate = useNavigate();
  const { sessionId } = useOnboardingSession();
  const { seekerId, isLoading: authLoading } = useSeekerAuth();
  const posthog = usePostHog();
  const [isSaving, setIsSaving] = useState(false);
  const [createAccountData, setCreateAccountData] = useState<CreateAccountData | null>(null);

  useEffect(() => {
    const directActive = localStorage.getItem('direct_connect_active') === 'true';
    if (!directActive) return;

    const directEmail = localStorage.getItem('direct_connect_email') || '';
    const directName = localStorage.getItem('direct_connect_name') || '';

    setAnswers((prev) => {
      const next = { ...prev };
      let changed = false;
      if (directEmail && prev['seeker_email'] !== directEmail) {
        next['seeker_email'] = directEmail;
        changed = true;
      }
      if (directName && prev['seeker_full_name'] !== directName) {
        next['seeker_full_name'] = directName;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [setAnswers]);

  // Restaurar createAccountData desde localStorage al recargar
  useEffect(() => {
    const stored = localStorage.getItem('create_account_data');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setCreateAccountData({
          firstName: parsed.firstName || '',
          email: parsed.email || '',
          confirmEmail: parsed.email || '',
          phone: parsed.phone || '',
          password: '',
          agreedToTerms: true,
          marketingOptIn: false,
        });
      } catch (e) {
        console.warn('Failed to restore create account data:', e);
      }
    }
  }, []);

  const currentIndex = sectionId ? allSteps.findIndex((step) => step.id === sectionId) : 0;

  // Visible steps for navigation (includes auth steps)
  const allVisibleStepIndexes = useMemo(
    () =>
      allSteps
        .map((step, index) => ({ step, index }))
        .filter((item) => isStepVisible(item.step, answers))
        .map((item) => item.index),
    [answers],
  );

  // Visible steps for progress bar (excludes auth steps)
  const visibleStepIndexes = useMemo(
    () =>
      allSteps
        .map((step, index) => ({ step, index }))
        .filter((item) => isStepVisible(item.step, answers))
        .filter(
          (item) => item.step.kind !== 'create-account' && item.step.kind !== 'email-verification',
        )
        .map((item) => item.index),
    [answers],
  );

  // Don't render if we don't have auth info yet
  if (authLoading || !seekerId) {
    return <div>Loading...</div>; // You might want a proper loading component here
  }

  if (currentIndex === -1) {
    return <Navigate to={`/onboarding/${allSteps[0].id}`} replace />;
  }

  const currentStep = allSteps[currentIndex];

  const visibleIndexPosition = visibleStepIndexes.indexOf(currentIndex);
  const totalVisibleSteps = visibleStepIndexes.length;

  if (!isStepVisible(currentStep, answers)) {
    const nextVisible = findNextVisibleIndex(currentIndex + 1, 1, answers);
    if (nextVisible !== -1) {
      navigate(`/onboarding/${allSteps[nextVisible].id}`, { replace: true });
      return null;
    }

    const previousVisible = findNextVisibleIndex(currentIndex - 1, -1, answers);
    if (previousVisible !== -1) {
      navigate(`/onboarding/${allSteps[previousVisible].id}`, { replace: true });
      return null;
    }

    navigate('/onboarding/safety', { replace: true });
    return null;
  }

  const handleAnswerChange = (id: string, value: string | string[] | boolean | undefined) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  // Function to save data at checkpoints
  const saveAtCheckpoint = async (questionId: string) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      if (questionId === 'seeker_phone') {
        const supabase = getSupabaseClient();
        const emailAnswer = answers['seeker_email'];
        const nameAnswer = answers['seeker_full_name'];
        const phoneAnswer = answers['seeker_phone'];

        const email = typeof emailAnswer === 'string' ? emailAnswer.trim().toLowerCase() : '';
        const fullName = typeof nameAnswer === 'string' ? nameAnswer.trim() : '';
        const phone = typeof phoneAnswer === 'string' ? phoneAnswer.trim() : '';

        if (email) {
          const { data: authInfo } = await supabase.auth.getUser();
          const profileId = authInfo?.user?.id || null;

          if (profileId) {
            const profilePayloads: Array<Record<string, any>> = [
              {
                id: profileId,
                email,
                full_name: fullName || null,
                phone: phone || null,
              },
              {
                id: profileId,
                email,
                full_name: fullName || null,
              },
            ];

            let profileCreated = false;
            for (const payload of profilePayloads) {
              const { error } = await supabase
                .from('profiles')
                .upsert(payload, { onConflict: 'id' });
              if (!error) {
                profileCreated = true;
                break;
              } else {
                console.warn('Profile upsert attempt failed', error);
              }
            }

            if (!profileCreated) {
              console.warn('Unable to create seeker profile after contact details');
            }
          }
        }

        try {
          const existingRaw = localStorage.getItem('basic_info_data');
          const existing = existingRaw ? JSON.parse(existingRaw) : {};
          const merged = {
            ...existing,
            seeker_id: seekerId,
            seeker_email: email,
            seeker_name: fullName,
            seeker_phone: phone,
          };
          localStorage.setItem('basic_info_data', JSON.stringify(merged));
        } catch (storageError) {
          console.warn('Unable to cache contact info locally', storageError);
        }

        const directActive = localStorage.getItem('direct_connect_active') === 'true';
        const directGuideId = localStorage.getItem('direct_connect_guide_id');
        if (directActive && directGuideId) {
          try {
            const supabase = getSupabaseClient();
            // Use edge function for RLS-compliant match creation
            const { data: matchData, error: matchError } = await supabase.functions.invoke(
              'direct-connect-upsert-match',
              {
                body: {
                  seeker_id: seekerId,
                  guide_id: directGuideId,
                  source: 'contact_info',
                },
              },
            );

            if (matchError) {
              console.warn('Failed to upsert direct-connect match after contact info', matchError);
            } else if (matchData?.success) {
              localStorage.setItem('direct_connect_match_created', 'true');
            }
          } catch (matchErr) {
            console.warn('Direct-connect match upsert failed (contact info)', matchErr);
          }
        }
      }

      // Check which checkpoint we're at
      if (questionId === 'location_postcode') {
        // Save basic info after completing option_types question
        const basicInfoData = SeekerOnboardingService.transformAnswersToBasicInfo(answers);
        await SeekerOnboardingService.saveBasicInfo(seekerId, sessionId, basicInfoData);
        console.log('✅ Basic info saved at checkpoint');

        try {
          const directActive = localStorage.getItem('direct_connect_active') === 'true';
          const guideId = localStorage.getItem('direct_connect_guide_id');
          if (directActive && guideId) {
            const supabase = getSupabaseClient();
            // Use edge function for RLS-compliant match creation
            const { data: matchData, error: matchError } = await supabase.functions.invoke(
              'direct-connect-upsert-match',
              {
                body: {
                  seeker_id: seekerId,
                  guide_id: guideId,
                  source: 'basic_info',
                },
              },
            );

            if (matchError) {
              console.warn('Unable to upsert direct-connect match', matchError);
            } else if (matchData?.success) {
              localStorage.setItem('direct_connect_match_created', 'true');
            }
          }
        } catch (matchErr) {
          console.warn('Direct-connect match creation failed', matchErr);
        }
      } else if (
        questionId === 'seeker_beliefs_other_text' ||
        (questionId === 'religion_alignment_important' &&
          answers.religion_alignment_important === 'No')
      ) {
        // Save experience preferences after completing the experience section
        const experienceData =
          SeekerOnboardingService.transformAnswersToExperiencePreferences(answers);
        await SeekerOnboardingService.saveExperiencePreferences(
          seekerId,
          sessionId,
          experienceData,
        );
        console.log('✅ Experience preferences saved at checkpoint');
      } else if (questionId === 'referral_source') {
        // Save final data when completing the entire onboarding (after last question)
        const medicalData = SeekerOnboardingService.transformAnswersToMedicalAndFinal(answers);
        await SeekerOnboardingService.saveMedicalAndFinal(seekerId, sessionId, medicalData);
        console.log('✅ Final onboarding data saved');
      }

      // Guardar en cada pregunta después de create-account (Opción 1)
      const createAccountCompleted = localStorage.getItem('create_account_completed') === 'true';
      if (createAccountCompleted) {
        const createAccountIndex = allSteps.findIndex((s) => s.id === 'create-account');
        const currentQuestionStep = allSteps.find(
          (s) => s.kind === 'question' && s.id === questionId,
        ) as QuestionStep | undefined;
        const currentQuestionIndex = allSteps.findIndex(
          (s) => s.kind === 'question' && s.id === questionId,
        );

        // Solo guardar si estamos después de create-account
        if (currentQuestionIndex > createAccountIndex && currentQuestionStep) {
          const sectionId = currentQuestionStep.section.id;

          if (sectionId === 'experience-info') {
            const experienceData =
              SeekerOnboardingService.transformAnswersToExperiencePreferences(answers);
            await SeekerOnboardingService.saveExperiencePreferences(
              seekerId,
              sessionId,
              experienceData,
            );
          } else if (sectionId === 'medical-info' || sectionId === 'additional-info') {
            const medicalData = SeekerOnboardingService.transformAnswersToMedicalAndFinal(answers);
            await SeekerOnboardingService.saveMedicalAndFinal(seekerId, sessionId, medicalData);
          }
        }
      }
    } catch (error) {
      console.error('Error saving at checkpoint:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSaving(false);
    }
  };

  const handleContinue = async () => {
    const currentStep = allSteps[currentIndex];

    // Check if user selected "No" for the over 18 question - redirect to not-a-fit page
    if (currentStep.kind === 'question' && currentStep.question.id === 'seeker_over_18') {
      const over18Answer = answers['seeker_over_18'];
      if (over18Answer === 'No') {
        navigate('/onboarding/not-a-fit', { replace: true });
        return;
      }
    }

    // Check email before continuing if this is the email question
    if (currentStep.kind === 'question' && currentStep.question.id === 'seeker_email') {
      const emailAnswer = answers['seeker_email'];
      const email = typeof emailAnswer === 'string' ? emailAnswer.trim().toLowerCase() : '';

      if (email) {
        try {
          const supabase = getSupabaseClient();
          const { data, error } = await supabase.functions.invoke('check-seeker-email', {
            body: { email },
          });

          if (!error && data) {
            // If not a new seeker, redirect to returning user page
            if (!data.newSeeker) {
              navigate('/onboarding/returning-user', {
                state: {
                  email,
                  onboardingStarted: data.onboardingStarted,
                  accountExists: data.accountExists,
                  isGuide: data.isGuide || false,
                },
              });
              return;
            }
          }
        } catch (err) {
          console.error('Failed to check email:', err);
          // Continue anyway if check fails
        }
      }
    }

    // Emit PostHog event for question progression
    if (currentStep.kind === 'question' && posthog) {
      try {
        const questionIndex = allSteps
          .filter((s) => s.kind === 'question')
          .findIndex((s) => s.id === currentStep.question.id);

        posthog.capture('onboarding-question-answered', {
          section_id: currentStep.section.id,
          question_id: currentStep.question.id,
          question_index: questionIndex + 1,
          seeker_profile_id: seekerId,
        });
      } catch (err) {
        console.warn('PostHog event capture failed:', err);
      }
    }

    // Save data at checkpoint if this is a checkpoint question
    if (currentStep.kind === 'question') {
      await saveAtCheckpoint(currentStep.question.id);
    }

    const nextVisible = findNextVisibleIndex(currentIndex + 1, 1, answers);
    if (nextVisible === -1) {
      const directFlowActive = localStorage.getItem('direct_connect_active');
      if (directFlowActive === 'true') {
        navigate('/connect/booking');
        return;
      }
      if (answers['location_postcode']) {
        navigate('/onboarding/terms');
      } else {
        navigate('/onboarding/no-match');
      }
    } else {
      navigate(`/onboarding/${allSteps[nextVisible].id}`);
    }
  };

  const handleBack = () => {
    const createAccountCompleted = localStorage.getItem('create_account_completed') === 'true';
    const currentStepDef = allSteps[currentIndex];

    // Si estamos en email-verification y create-account ya fue completado,
    // no permitir regresar a create-account, ir al checkpoint
    if (currentStepDef.id === 'email-verification' && createAccountCompleted) {
      const checkpointIndex = allSteps.findIndex((s) => s.id === 'location_postcode');
      if (checkpointIndex !== -1) {
        navigate(`/onboarding/${allSteps[checkpointIndex].id}`);
        return;
      }
    }

    const previousVisible = findNextVisibleIndex(currentIndex - 1, -1, answers);
    if (previousVisible === -1) {
      navigate('/', { replace: true });
    } else {
      const previousStep = allSteps[previousVisible];
      // Si el paso anterior es create-account o email-verification y ya se completó,
      // saltar al checkpoint
      if (
        (previousStep.id === 'create-account' || previousStep.id === 'email-verification') &&
        createAccountCompleted
      ) {
        const checkpointIndex = allSteps.findIndex((s) => s.id === 'location_postcode');
        if (checkpointIndex !== -1) {
          navigate(`/onboarding/${allSteps[checkpointIndex].id}`);
          return;
        }
      }
      navigate(`/onboarding/${allSteps[previousVisible].id}`);
    }
  };

  // Check if current step is in the full visible list (including auth steps)
  const isCurrentStepVisible = allVisibleStepIndexes.includes(currentIndex);
  if (!isCurrentStepVisible) {
    const fallback = findNextVisibleIndex(currentIndex + 1, 1, answers);
    if (fallback !== -1) {
      navigate(`/onboarding/${allSteps[fallback].id}`, { replace: true });
    } else {
      navigate('/onboarding/safety', { replace: true });
    }
    return null;
  }

  const progressLabelBase =
    currentStep.kind === 'question'
      ? currentStep.section.title
      : currentStep.kind === 'checkpoint' ||
          currentStep.kind === 'create-account' ||
          currentStep.kind === 'email-verification'
        ? currentStep.progressSection.title
        : '';
  const progressLabel = `Step ${visibleIndexPosition + 1} of ${totalVisibleSteps}`;

  if (currentStep.kind === 'checkpoint') {
    if (currentStep.variant === 'medication' && currentStep.acknowledgements) {
      return (
        <MedicationSafetyCheckpoint
          stepIndex={visibleIndexPosition}
          totalSteps={totalVisibleSteps}
          progressLabel={progressLabel}
          title={currentStep.title}
          subtitle={currentStep.subtitle}
          detailHeading={currentStep.detailHeading}
          detailBody={currentStep.detailBody}
          acknowledgements={currentStep.acknowledgements}
          onBack={handleBack}
          onContinue={handleContinue}
        />
      );
    }

    return (
      <OnboardingCheckpointStep
        stepIndex={visibleIndexPosition}
        totalSteps={totalVisibleSteps}
        progressLabel={progressLabel}
        title={currentStep.title}
        subtitle={currentStep.subtitle}
        detailHeading={currentStep.detailHeading}
        detailBody={currentStep.detailBody}
        nextHeading={currentStep.nextHeading}
        nextBody={currentStep.nextBody}
        guideCountPostcode={
          typeof answers['location_postcode'] === 'string'
            ? (answers['location_postcode'] as string)
            : undefined
        }
        onBack={handleBack}
        onContinue={handleContinue}
      />
    );
  }

  if (currentStep.kind === 'create-account') {
    const handleCreateAccountContinue = async (data: CreateAccountData) => {
      // 1) Persist create-account fields onto seeker_basic_info so the edge
      //    function can read email/name/phone when creating the auth user.
      //    Merges with anything already stored via (seeker_id, session_id)
      //    upsert conflict target.
      const updatedBasicInfo = {
        ...SeekerOnboardingService.transformAnswersToBasicInfo(answers),
        seeker_email: data.email,
        seeker_full_name: data.firstName,
        seeker_phone: data.phone,
      };

      await SeekerOnboardingService.saveBasicInfo(seekerId, sessionId, updatedBasicInfo);

      // 2) Create the real Supabase auth user. On prod this triggers a
      //    confirmation email; on staging (where email confirmation is
      //    disabled) it creates an already-confirmed user and no email
      //    is sent. Throws on failure; the CreateAccount step surfaces
      //    the error inline.
      await SeekerOnboardingService.createSeekerAccount(
        seekerId,
        data.password,
        data.marketingOptIn ?? false,
      );

      // 3) Try to sign the seeker in right away. In environments with email
      //    confirmation disabled (staging) this succeeds and we can skip the
      //    verification step entirely. In prod it returns "Email not
      //    confirmed" and we continue to the verification flow as normal.
      const { skipVerification } = await SeekerOnboardingService.trySignInAfterSignup(
        data.email,
        data.password,
      );

      // 4) Stash account data locally so the verification step (if reached)
      //    knows which email to display / resend to. Never store the password.
      setCreateAccountData(data);
      localStorage.setItem('create_account_completed', 'true');
      localStorage.setItem(
        'create_account_data',
        JSON.stringify({
          email: data.email,
          firstName: data.firstName,
          phone: data.phone,
        }),
      );

      // 5) Navigate. If we already have a session, skip past the verification
      //    step. Otherwise go to verification as normal.
      const startFrom = skipVerification ? currentIndex + 2 : currentIndex + 1;
      const nextVisible = findNextVisibleIndex(startFrom, 1, answers);
      if (nextVisible !== -1) {
        navigate(`/onboarding/${allSteps[nextVisible].id}`);
      } else if (skipVerification) {
        // End of flow (no steps after verification) — fall through to
        // terms/no-match like the verification handler does.
        if (answers['location_postcode']) {
          navigate('/onboarding/terms');
        } else {
          navigate('/onboarding/no-match');
        }
      }
    };

    // Prefill when returning from the email verification step ("Update email
    // address"). Prefer in-memory state; fall back to localStorage on reload.
    // Password is never stored, so the user always has to re-enter it.
    const storedAccountForPrefill = (() => {
      try {
        const raw = localStorage.getItem('create_account_data');
        return raw
          ? (JSON.parse(raw) as { email?: string; firstName?: string; phone?: string })
          : null;
      } catch {
        return null;
      }
    })();
    const createAccountInitialData = createAccountData
      ? {
          firstName: createAccountData.firstName,
          email: createAccountData.email,
          phone: createAccountData.phone,
        }
      : storedAccountForPrefill
        ? {
            firstName: storedAccountForPrefill.firstName,
            email: storedAccountForPrefill.email,
            phone: storedAccountForPrefill.phone,
          }
        : undefined;

    return (
      <OnboardingCreateAccountStep
        onBack={handleBack}
        onContinue={handleCreateAccountContinue}
        initialData={createAccountInitialData}
      />
    );
  }

  if (currentStep.kind === 'email-verification') {
    // If the user already confirmed their email (e.g. clicked the confirmation link),
    // skip the code entry and advance to the next onboarding step.
    // We check email_confirmed_at — a session can exist before confirmation (from signUp).
    const supabase = getSupabaseClient();
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email_confirmed_at) {
        console.log('Email already confirmed via link, skipping verification step');
        const nextVisible = findNextVisibleIndex(currentIndex + 1, 1, answers);
        if (nextVisible !== -1) {
          navigate(`/onboarding/${allSteps[nextVisible].id}`, { replace: true });
        } else {
          if (answers['location_postcode']) {
            navigate('/onboarding/terms', { replace: true });
          } else {
            navigate('/onboarding/no-match', { replace: true });
          }
        }
      }
    });

    // Prefer in-memory state (from the just-completed create-account step),
    // fall back to localStorage if the user reloaded mid-flow.
    const storedAccount = (() => {
      try {
        const raw = localStorage.getItem('create_account_data');
        return raw ? (JSON.parse(raw) as { email?: string }) : null;
      } catch {
        return null;
      }
    })();
    const emailToVerify = createAccountData?.email || storedAccount?.email || '';

    const handleEmailVerificationSubmit = async (code: string) => {
      if (!emailToVerify) {
        throw new Error('Missing email address. Please go back and re-enter your details.');
      }

      // Verify the OTP with Supabase. On success a session is established on
      // the browser client (so subsequent matching/personalization calls work).
      await SeekerOnboardingService.verifyEmailOtp(emailToVerify, code);

      const nextVisible = findNextVisibleIndex(currentIndex + 1, 1, answers);
      if (nextVisible !== -1) {
        navigate(`/onboarding/${allSteps[nextVisible].id}`);
      } else {
        // End of onboarding
        if (answers['location_postcode']) {
          navigate('/onboarding/terms');
        } else {
          navigate('/onboarding/no-match');
        }
      }
    };

    const handleResendCode = async () => {
      if (!emailToVerify) {
        throw new Error('Missing email address. Please go back and re-enter your details.');
      }
      await SeekerOnboardingService.resendSignupOtp(emailToVerify);
    };

    const handleUpdateEmail = () => {
      // Navigate directly to create-account step
      navigate('/onboarding/create-account');
    };

    return (
      <OnboardingEmailVerificationStep
        email={emailToVerify}
        onSubmit={handleEmailVerificationSubmit}
        onResend={handleResendCode}
        onUpdateEmail={handleUpdateEmail}
      />
    );
  }

  // At this point, currentStep must be a QuestionStep
  if (currentStep.kind !== 'question') {
    // Fallback for any unhandled step types
    return <Navigate to={`/onboarding/${allSteps[0].id}`} replace />;
  }

  return (
    <OnboardingQuestionStep
      section={currentStep.section}
      question={currentStep.question}
      stepIndex={visibleIndexPosition}
      totalSteps={totalVisibleSteps}
      progressLabel={progressLabel}
      answers={answers}
      onAnswerChange={handleAnswerChange}
      onBack={handleBack}
      onContinue={handleContinue}
    />
  );
}
